/**
 * Configurateur produit heycup — écran 4a.
 *
 * Ce fichier ne fait que relier le DOM à la grille : toute la décision — quelle
 * combinaison existe, pourquoi une autre est fermée, quel palier fait baisser
 * le prix — vit dans `hey-grille.js`, sans DOM et donc testable.
 *
 * Toutes les combinaisons vendables sont déjà dans la page (voir
 * `snippets/hey-configurateur-donnees.liquid`). Aucun appel réseau n'est fait
 * au changement de sélection, ce qu'exige le cahier des charges.
 */

import { Component } from '@theme/component';
import { Grille, formaterMontant, formaterPieces } from './hey-grille.js';

class HeyConfigurateur extends Component {
  /** @type {Grille | null} */
  #grille = null;

  /** Le libelle rendu par Liquid, pour y revenir quand plus rien ne correspond. */
  #libelleTotalParDefaut = '';

  connectedCallback() {
    super.connectedCallback();

    const balise = this.querySelector('[data-hey-configurateur-donnees]');
    if (!balise) return;

    try {
      this.#grille = new Grille(JSON.parse(balise.textContent));
    } catch (e) {
      // Sans grille, on laisse la page telle quelle : les sélecteurs restent
      // utilisables et le formulaire garde la variante rendue par Liquid.
      console.error('[heycup] grille de variantes illisible', e);
      return;
    }

    this.#libelleTotalParDefaut =
      this.querySelector('[data-hey-champ="recap-total-label"]')?.textContent ?? '';

    this.#afficherMinimums();
    this.recalculer();
  }

  get #choix() {
    return Array.isArray(this.refs.choix) ? this.refs.choix : [];
  }

  get #defaut() {
    return this.dataset.libelleIndisponible || "Cette combinaison n'existe pas.";
  }

  /** La sélection courante : un libellé par axe. */
  #selection() {
    const sel = new Array(this.#grille.noms.length).fill(null);
    for (const input of this.#choix) {
      if (input.checked) sel[Number(input.dataset.heyAxe)] = input.value;
    }
    return sel;
  }

  surChangement() {
    this.recalculer();
  }

  recalculer() {
    if (!this.#grille) return;
    const sel = this.#selection();
    const variante = this.#grille.variante(sel);

    this.#majDisponibilites(sel);
    this.#majPrix(sel, variante);
    this.#majAchat(variante);
    this.#majPalierSuivant(sel);
    this.#majLienDevis(sel, variante);
    this.#annoncer(sel, variante);
  }

  /**
   * Annonce la configuration courante aux blocs voisins — la prestation
   * graphique s'en sert pour savoir si la creation est offerte.
   *
   * Un evenement plutot qu'une lecture du DOM : deviner le palier en
   * cherchant le mot « pieces » dans une valeur d'option casserait au premier
   * changement de libelle.
   */
  #annoncer(sel, variante) {
    const pieces = variante ? this.#grille.pieces(variante.v[this.#grille.axes.palier]) : 0;
    this.dataset.heyPieces = String(pieces);
    this.dispatchEvent(
      new CustomEvent('hey:configuration', {
        bubbles: true,
        detail: { pieces, selection: sel, variante },
      })
    );
  }

  /** Le minimum d'une technique ne dépend pas de la sélection : une seule fois. */
  #afficherMinimums() {
    const axeMarquage = this.#grille.axes.marquage;
    if (axeMarquage < 0) return;

    for (const input of this.#choix) {
      if (Number(input.dataset.heyAxe) !== axeMarquage) continue;
      const cible = input.closest('[data-hey-valeur]')?.querySelector('[data-hey-champ="minimum"]');
      if (!cible) continue;
      const p = this.#grille.minimumDuMarquage(input.value);
      cible.textContent = p ? `dès ${formaterPieces(p.pieces)} pièces` : '';
    }
  }

  /* --------------------------------------------------------------------
     Disponibilité : une valeur est grisée si, les autres axes restant tels
     quels, aucune variante n'existe. Elle n'est jamais retirée du DOM, et
     elle porte toujours la raison de sa fermeture.
     -------------------------------------------------------------------- */
  #majDisponibilites(sel) {
    for (const input of this.#choix) {
      const axe = Number(input.dataset.heyAxe);
      const essai = sel.slice();
      essai[axe] = input.value;

      const variante = this.#grille.variante(essai);
      const possible = variante != null;

      const etiquette = input.closest('[data-hey-valeur]');
      if (!etiquette) continue;

      // `aria-disabled` et non `disabled` : une option vraiment desactivee
      // sort du parcours clavier, et l'utilisateur au lecteur d'ecran
      // n'entend jamais POURQUOI elle est fermee — alors que c'est
      // exactement ce que le cahier demande d'expliquer. Elle reste donc
      // atteignable et selectionnable ; c'est le recapitulatif qui porte le
      // blocage, et le bouton d'ajout qui refuse.
      input.setAttribute('aria-disabled', String(!possible));
      etiquette.classList.toggle('hey-conf__option--indisponible', !possible);

      const raison = etiquette.querySelector('[data-hey-champ="raison"]');
      if (raison) {
        raison.textContent = possible ? '' : this.#grille.raison(axe, input.value, sel, this.#defaut);
      }

      // Le prix que CETTE valeur donnerait, les autres axes inchangés.
      const cibleUnit = etiquette.querySelector('[data-hey-champ="unitaire"]');
      const cibleTotal = etiquette.querySelector('[data-hey-champ="total"]');
      // `unitaire()` rend 0 quand le nombre de pièces est introuvable, et
      // `formaterMontant(0)` écrit « 0.00 ». Un prix nul affiché est pire qu'un
      // tiret : le tiret dit « pas de prix ici », le zéro dit « c'est
      // gratuit ». Le Liquid écrit déjà le tiret dans ce cas ; le script fait
      // pareil, sinon les deux rendus se contredisent au chargement.
      const unit = variante ? this.#grille.unitaire(variante) : 0;
      if (cibleUnit) cibleUnit.textContent = unit ? formaterMontant(unit) : '—';
      if (cibleTotal) cibleTotal.textContent = variante ? formaterMontant(variante.prix) : '—';
    }
  }

  /* --------------------------------------------------------------------
     Prix — le cœur de la promesse commerciale : toujours visible.
     -------------------------------------------------------------------- */
  /* Écrire dans une région `aria-live` ce qui s'y trouve déjà la fait annoncer
     pour rien. Depuis que le serveur rend le récapitulatif, le premier passage
     du script y réécrit exactement les mêmes montants — et un lecteur d'écran
     lisait donc un prix que personne n'avait demandé, à l'ouverture de la page.
     On n'écrit que si la valeur change. Même discipline que dans
     `hey-panier.js`, pour la même raison. */
  static #poser(noeud, valeur) {
    if (noeud && noeud.textContent !== valeur) noeud.textContent = valeur;
  }

  #majPrix(sel, variante) {
    const poser = HeyConfigurateur.#poser;
    const unitaire = this.querySelector('[data-hey-champ="recap-unitaire"]');
    const total = this.querySelector('[data-hey-champ="recap-total"]');
    const labelTotal = this.querySelector('[data-hey-champ="recap-total-label"]');
    const resume = this.querySelector('[data-hey-champ="resume"]');

    if (!variante) {
      poser(unitaire, '—');
      poser(total, '—');
      // Le libelle du total aussi : sinon il continue d'annoncer le palier
      // precedent au-dessus d'un tiret, ce qui se lit comme un prix.
      poser(labelTotal, this.#libelleTotalParDefaut);
      poser(resume, '');
      return;
    }

    const pieces = this.#grille.pieces(variante.v[this.#grille.axes.palier]);
    const unitRecap = this.#grille.unitaire(variante);
    poser(unitaire, unitRecap ? formaterMontant(unitRecap) : '—');
    poser(total, formaterMontant(variante.prix));
    poser(labelTotal, `Total ${formaterPieces(pieces)} pièces`);
    if (resume) resume.textContent = sel.filter(Boolean).join(' · ');
  }

  /* --------------------------------------------------------------------
     Achat : le bouton suit exactement la disponibilité réelle, et l'entrée
     cachée du formulaire porte toujours la variante affichée.
     -------------------------------------------------------------------- */
  #majAchat(variante) {
    const form = document.getElementById(this.dataset.formId);
    // Vide quand rien ne correspond : un identifiant perime laisserait
    // passer la MAUVAISE variante si le bouton etait reactive par ailleurs.
    // Un champ vide fait echouer /cart/add bruyamment, ce qui se voit.
    const champId = form?.querySelector('input[name="id"]');
    if (champId) champId.value = variante ? String(variante.id) : '';

    const bouton = this.querySelector('add-to-cart-component button[type="submit"]');
    if (bouton) bouton.disabled = !variante;

    const blocage = this.querySelector('[data-hey-champ="blocage"]');
    if (!blocage) return;

    if (variante) {
      blocage.hidden = true;
      blocage.textContent = '';
      return;
    }

    // On reprend l'explication déjà calculée pour le palier retenu, plutôt
    // que d'en produire une seconde : deux phrases différentes pour la même
    // situation, au même endroit, sèment le doute.
    const coche = this.#choix.find(
      (i) => i.checked && Number(i.dataset.heyAxe) === this.#grille.axes.palier
    );
    const raison = coche?.closest('[data-hey-valeur]')?.querySelector('[data-hey-champ="raison"]')?.textContent;
    blocage.textContent = raison || this.#defaut;
    blocage.hidden = false;
  }

  /* --------------------------------------------------------------------
     Palier suivant : un levier de panier moyen, pas une décoration.
     -------------------------------------------------------------------- */
  #majPalierSuivant(sel) {
    const cible = this.querySelector('[data-hey-champ="palier-suivant"]');
    if (!cible) return;

    const suivant = this.#grille.palierSuivant(sel);
    if (!suivant) {
      cible.hidden = true;
      cible.textContent = '';
      return;
    }

    cible.textContent = (cible.dataset.modele || '')
      .replace('[palier]', formaterPieces(suivant.pieces))
      .replace('[prix]', formaterMontant(suivant.unitaire))
      .replace('[prix_actuel]', formaterMontant(suivant.unitaireActuel))
      .replace('[economie]', String(suivant.economie));
    cible.hidden = false;
  }

  /* --------------------------------------------------------------------
     Devis : la configuration part dans l'URL, pour que le formulaire la
     reprenne sans que le client la ressaisisse.
     -------------------------------------------------------------------- */
  #majLienDevis(sel, variante) {
    const lien = this.querySelector('[data-hey-champ="lien-devis"]');
    const base = lien?.dataset.base;
    if (!lien || !base) return;

    const p = new URLSearchParams();
    if (this.#grille.ref) p.set('ref', this.#grille.ref);
    this.#grille.noms.forEach((nom, i) => {
      if (sel[i]) p.set(String(nom).toLowerCase(), sel[i]);
    });
    if (variante) {
      if (variante.sku) p.set('sku', variante.sku);
      p.set('total', formaterMontant(variante.prix));
    }
    lien.href = `${base}${base.includes('?') ? '&' : '?'}${p}`;
  }
}

if (!customElements.get('hey-configurateur')) {
  customElements.define('hey-configurateur', HeyConfigurateur);
}
